// React core imports
import React, { useEffect, useState, useRef } from 'react';
import { NavLink } from 'react-router-dom';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Third-party libraries
import { Row, Col, Dropdown, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
// Page metadata
import HtmlHead from 'components/html-head/HtmlHead';

// Utility functions
import { handlerErrors } from 'utils/handleErrors';

// API functions
import { getSupportTickets, getFilteredSupportTickets } from 'api/Api-user';

// UI Components
import { ToastErrorCath } from 'components/alerts/toast';
import Paginate from 'components/Paginate';
import EmptyPage from 'components/emptyStates/EmptyPage';
import EmptyFilterResult from 'components/emptyStates/EmptyFilterResult';
import ConnectionError from 'components/emptyStates/ConnectionError';
// Loading Components
import LoadingPulse from 'components/loading/LoadingPulse';
import LoadingCar from 'components/loading/LoadingCar';
import ProvisionalUserAlert from '../../components/alerts/UserAlert';

// Page-specific Components
import CardIncident from './components/CardIncident';
import ReportModal from '../../components/modals/ReportModal';

// Redux Actions
import { setPage, setItemsPerPage, setStatus, setFilteredMode } from '../../slices/uiStates/reportsSlice';

const { FormatMoney } = require('format-money-js');

const Reports = () => {
  const title = 'Reportes';
  const description = 'Reporte Incidentes Plataforma Autopaquete';
  const dispatch = useDispatch();

  // - Global State from Store.js
  const { page, itemsPerPage, status, filteredMode } = useSelector((state) => state.reports);

  // -Local States
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [tickets, setTickets] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [pageView, setPageView] = useState(page);
  const [itemsPerPageView, setItemsPerPageView] = useState(itemsPerPage);
  const [totalItems, setTotalItems] = useState(0);
  const [emptyHistory, setEmptyHistory] = useState(false);
  const [hasConnectionIssue, setHasConnectionIssue] = useState(false);

  // - Filtering states
  const [filteredSuccess, setFilteredSuccess] = useState('');

  // - Use references to do scroll Up
  const startRef = useRef(null);
  const fm = new FormatMoney({
    decimals: 0, // No decimales
  });
  const settingPage = (limit) => {
    dispatch(setItemsPerPage(limit)); // ejecuta el useEffect que decide que funcion ejecutar
    dispatch(setPage(1)); // ejecuta el useEffect que decide que funcion ejecutar
  };

  // - Get all pickups
  const handlerGetTickets = async () => {
    try {
      if (tickets) {
        setIsLoading(true);
      }
      const result = await getSupportTickets(page, itemsPerPage);
      // console.log('response reports:', result);
      if (result.tickets !== undefined) {
        setTickets(result.tickets);
        setPageView(result.current_page); // para actualizar la vista
        setItemsPerPageView(result.page_size); // para actualizar la vista
        setTotalItems(result.total_items);
        setTotalPages(result.total_pages);
        setFilteredSuccess(''); //  para eliminar el mensaje "Filtro por servicio..."
        if (startRef.current) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        if (result.tickets.length === 0) {
          setEmptyHistory(tickets || true); // para mostrar mensaje de "No tienes historial"
        }
      }
    } catch (error) {
      setTickets(tickets || []);
      setFilteredSuccess(''); // para eliminar el mensaje "Filtro por servicio..."
      setHasConnectionIssue(true);
      handlerErrors(error);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };
  // - Search reports by filters
  const handlerGetFilteredReports = async () => {
    if (!status.trim()) {
      dispatch(setFilteredMode(false)); // si changePage es mayor a 1 lo pone en true el btn buscar pero aqui es error esto es para que la paginacion siga funcionando en modo no filtrando
      ToastErrorCath(`Error: Por favor, ingresa la información necesaria para filtrar.`);
      return;
    }
    if (tickets) {
      setIsLoading(true);
    }
    try {
      const result = await getFilteredSupportTickets(page, itemsPerPage, status);
      // console.log('getFilteredSupportTickets result:', result);
      if (result.tickets !== undefined) {
        setTickets(result.tickets);
        setPageView(result.current_page); // para actualizar la vista
        setItemsPerPageView(result.page_size); // para actualizar la vista
        setTotalItems(result.total_items);
        setTotalPages(result.total_pages);
        setFilteredSuccess(
          `${status ? `Estado de reporte ${status === 'Solución favorable' || status === 'Solución desfavorable' ? 'cerrado con' : ''} ▸ ${status} ` : ''} `
        );

        if (startRef.current && startRef.current.scrollIntoView) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } catch (error) {
      handlerErrors(error);
      setTickets([]);
      setHasConnectionIssue(true);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
      dispatch(setFilteredMode(true)); // respuesta exitosa o no es necesario para mantener el modo filtrando y que aparezca el btn regresar
    }
  };
  const returnInitialMode = () => {
    // handlerGetTickets() o  handlerGetFilteredReports() activan el loader
    dispatch(setFilteredMode(false));
    dispatch(setStatus(''));
    if (page > 1) {
      dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
    } else {
      // si changePage es 1 no ejecuta el cambio de estado ni el useEffect es necesario llamar a la funcion
      handlerGetTickets();
    }
  };
  useEffect(() => {
    if (filteredMode) {
      handlerGetFilteredReports();
    } else {
      handlerGetTickets();
    }
  }, [page, itemsPerPage]);

  return (
    <>
      {tickets === null && <LoadingCar />}
      {isLoading && <LoadingPulse />}
      <ReportModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />

      <div id="pageContainer">
        <HtmlHead title={title} description={description} />
        <div className="page-title-container" ref={startRef}>
          <Row className="g-0 mb-3 mb-sm-0 d-flex align-items-end">
            {/* Title Start */}
            <Col className="col-auto me-auto">
              <NavLink className="muted-link pb-1 d-inline-block hidden breadcrumb-back" to="/">
                <CsLineIcons icon="chevron-left" size="13" />
                <span className="align-middle text-small ms-1">Inicio</span>
              </NavLink>
              <h1 className="mb-0 pb-0 display-4" id="title">
                {title}
              </h1>
            </Col>
            {/* Title End */}
            <Col xs="auto">
              {/* Button Create Ticket  */}
              <Button variant="primary" style={{ height: '36px' }} className="d-flex align-items-center" onClick={() => setIsModalOpen(true)}>
                <CsLineIcons icon="plus" size="14" /> <span className="ms-1">Crear reporte</span>
              </Button>
            </Col>
          </Row>
        </div>
        <div style={{ minHeight: '50vh' }} id="cardContainer">
          {/* Elements for Filtering Start */}
          <Row className="g-0 mt-sm-5 d-flex align-items-end bg-infox">
            {/* Filters Start */}
            <Col xs="12" lg="9" className="bg-warningx">
              <Row className="g-0">
                <Col xs="auto" className="mb-0 me-2 mb-2 mb-lg-0 bg-alternatex">
                  <Dropdown className="d-inline-block ">
                    <Dropdown.Toggle
                      variant="foreground-alternate"
                      className="shadow d-flex align-items-center justify-content-between sw-26"
                      style={{ minWidth: '200px' }}
                    >
                      {status || 'Estado'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow sw-auto" style={{ minHeight: '100px', maxHeight: '350px', overflowY: 'scroll', fontFamily: 'Montserrat' }}>
                      {/* <Dropdown.Item onClick={() => setTicketState('')}>Ninguno</Dropdown.Item> */}
                      <Dropdown.Item onClick={(e) => dispatch(setStatus(e.target.innerText))} className="d-flex justify-content-between align-item-center">
                        <span>Abierto</span>
                        <OverlayTrigger
                          delay={{ show: 500, hide: 0 }}
                          placement="top"
                          overlay={
                            // .tooltip this is its class
                            <Tooltip id="tooltip-top">Reporte recibido. Está siendo revisado por nuestro equipo.</Tooltip>
                          }
                        >
                          <sapn>
                            <CsLineIcons icon="info-hexagon" size="18" className="text-alternate mb-1" />
                          </sapn>
                        </OverlayTrigger>
                      </Dropdown.Item>
                      <Dropdown.Item onClick={(e) => dispatch(setStatus(e.target.innerText))} className="d-flex justify-content-between align-item-center">
                        <span>Escalado</span>
                        <OverlayTrigger
                          delay={{ show: 500, hide: 0 }}
                          placement="top"
                          overlay={
                            // .tooltip this is its class
                            <Tooltip id="tooltip-top">Reporte enviado a la paquetería para su atención.</Tooltip>
                          }
                        >
                          <sapn>
                            <CsLineIcons icon="info-hexagon" size="18" className="text-alternate mb-1 ms-2" />
                          </sapn>
                        </OverlayTrigger>
                      </Dropdown.Item>
                      <Dropdown.Item onClick={(e) => dispatch(setStatus(e.target.innerText))} className="d-flex justify-content-between align-item-center">
                        <soan>Solución favorable</soan>
                        <OverlayTrigger
                          delay={{ show: 500, hide: 0 }}
                          placement="top"
                          overlay={
                            // .tooltip this is its class
                            <Tooltip id="tooltip-top">
                              {' '}
                              Reporte <strong>cerrado</strong> con una <strong>solución favorable</strong> por parte de la paquetería.
                            </Tooltip>
                          }
                        >
                          <sapn>
                            <CsLineIcons icon="info-hexagon" size="18" className="text-alternate mb-1" />
                          </sapn>
                        </OverlayTrigger>
                      </Dropdown.Item>
                      <Dropdown.Item onClick={(e) => dispatch(setStatus(e.target.innerText))} className="d-flex justify-content-between align-item-center">
                        <span>Solución desfavorable</span>
                        <OverlayTrigger
                          delay={{ show: 500, hide: 0 }}
                          placement="top"
                          overlay={
                            // .tooltip this is its class
                            <Tooltip id="tooltip-top">
                              Reporte <strong>cerrado</strong> con una <strong>solución desfavorable</strong> por parte de la paquetería.
                            </Tooltip>
                          }
                        >
                          <sapn>
                            <CsLineIcons icon="info-hexagon" size="18" className="text-alternate mb-1 ms-3" />
                          </sapn>
                        </OverlayTrigger>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                <Col xs="auto">
                  <Row className="g-0">
                    <Col xs="auto" className="pe-2">
                      <Button
                        variant="primary"
                        style={{ height: '36px' }}
                        className="w-100"
                        onClick={() => {
                          if (pageView > 1) {
                            // la primera vez el estado filteredMode es falso
                            dispatch(setFilteredMode(true)); // para que el useEffect funcione bien
                            dispatch(setPage(1)); // ejecuta el useEffect que decide que funcion ejecutar
                          } else {
                            handlerGetFilteredReports();
                          }
                        }}
                      >
                        <span>Buscar</span>
                      </Button>
                    </Col>
                    {filteredMode && (
                      <Col xs="auto">
                        <Button variant="outline-primary" style={{ height: '36px' }} className="w-100" onClick={returnInitialMode}>
                          <span>Regresar</span>
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>
            {/* Filters End */}
            <Col xs="12" lg="3" className="mt-2 mt-lg-0 d-flex justify-content-end align-items-end bg-secondaryx">
              {/* Dropdown  */}
              <Dropdown align={{ xs: 'end' }} className={`d-inline-block ms-1 ${tickets?.length < 2 && 'd-none'}`}>
                <Dropdown.Toggle variant="foreground-alternate" className="shadow sw-15">
                  {`${itemsPerPageView} reportes`}
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow dropdown-menu-end">
                  <Dropdown.Item className={`${itemsPerPageView === 10 && 'd-none'}`} onClick={() => settingPage(10)}>
                    10 reportes
                  </Dropdown.Item>
                  <Dropdown.Item className={`${itemsPerPageView === 20 && 'd-none'}`} onClick={() => settingPage(20)}>
                    20 reportes
                  </Dropdown.Item>
                  <Dropdown.Item className={`${itemsPerPageView === 50 && 'd-none'}`} onClick={() => settingPage(50)}>
                    50 reportes
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {/* <div className={`${statements?.length === 0 && 'invisible'} text-muted my-3 d-lg-flex`}>{`Página ${currentPage}`}</div> */}
            </Col>
          </Row>
          {/* Elements for Filtering End */}
          <hr className="bg-muted mb-3 mt-3 mt-xl-4" />
          {filteredSuccess && <div className="text-muted mb-3">{filteredSuccess}</div>}
          <div className="d-flex justify-content-between mb-3 text-muted">
            <span>
              Total: <strong>{fm.from(totalItems)}</strong> {`reportes ${!filteredMode ? '' : 'encontrados'} `}
            </span>
            <span className={`${tickets?.length < 2 && 'd-none'}`}>
              Página <strong>{pageView}</strong>
            </span>
          </div>
          <ProvisionalUserAlert title="Historial de reportes" />

          {/* List Header Start */}
          <Row className={`g-0 mb-2 d-flex align-items-center ps-5 pe-2 d-none d-lg-flex ${tickets?.length === 0 && 'invisible'}`}>
            <Col lg="4" className="d-flex flex-column mb-lg-0 pe-3 d-flex bg-infow">
              <div className="text-muted text-small cursor-pointer">REPORTE / ID ENVÍO</div>
            </Col>
            <Col lg="2" className="d-flex flex-column pe-1 justify-content-center align-items-start bg-alternatew">
              <div className="text-muted text-small cursor-pointer">FECHA CREACIÓN</div>
            </Col>
            <Col lg="1" className="d-flex flex-column pe-1 justify-content-center align-items-center bg-successw">
              <div className="text-muted text-small cursor-pointer">ID REPORTE</div>
            </Col>
            <Col lg="2" className="d-flex flex-column pe-1 ps-3 justify-content-center align-items-end bg-successw">
              <div className="text-muted text-small cursor-pointer">ULTIMA ACTUALIZACIÓN</div>
            </Col>
            <Col lg="1" className="d-flex flex-column pe-1 justify-content-center align-items-lg-end bg-alternatew">
              <div className="text-muted text-small text-end cursor-pointer">AGENTE</div>
            </Col>
            <Col lg="2" className="d-flex flex-column pe-1 justify-content-center mb-2 mb-lg-0 order-2 order-lg-last bg-secondaryw">
              <div className="text-muted text-small text-end cursor-pointer">ESTADO</div>
            </Col>
          </Row>
          {/* List Header End */}
          {tickets?.length === 0 && hasConnectionIssue && <ConnectionError refreshBtn />}
          {emptyHistory && (
            <EmptyPage text="Sin reportes por ahora. ¿Tuviste un problema con tu envío? Crea un reporte y nosotros nos ocupamos." showAnimation />
          )}
          {/* List Items Start */}
          {tickets?.length === 0 && filteredSuccess && <EmptyFilterResult />}
          {tickets?.length > 0 && (
            <>
              {/* List tickets Items Start */}
              {tickets?.map((ticket) => (
                <CardIncident key={ticket.id} ticket={ticket} />
              ))}
              {/* List tickets Items End */}
              {/* Pagination Start */}
              <div className={`d-flex flex-column justify-content-center align-items-center mt-5 mx-auto ${totalPages < 2 && 'd-none'}`}>
                <Paginate currentPage={page} totalPages={totalPages} setPageReducer={setPage} />
              </div>
              {/* Pagination End */}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Reports;
